import { type FC, type PropsWithChildren, useContext } from 'react';

import type { QueryClient } from '@tanstack/react-query';

import type { AppConfig } from '@cognite/apm-config';
import { ToastContainer } from '@cognite/cogs-lab-v11';
import { ConfigProvider } from '@cognite/cogs-core-v11/components/config-provider/config-provider.tsx';
import type { AuthStateUser } from '@cognite/e2e-auth';
import type { CogniteClient } from '@cognite/sdk';
import { SDKProvider } from '@cognite/sdk-provider';

import { getRootContainer } from '../pages/utils';

import { AccessProvider } from './access-provider/access-provider';
import { AppConfigFiltersProvider } from './app-config-filters-provider/app-config-filters-provider';
import { BackButtonUrlProvider } from './back-button-url-provider/back-button-url-provider';
import type { ClassicProvidersContextInterface } from './classic-providers.context';
import { ClassicProvidersContext } from './classic-providers.context';
import { CurrentUserProvider } from './current-user-provider/current-user-provider';
import { FDMServicesProvider } from './fdm-services/fdm-services-provider';
import { AppConfigProvider } from './is-idm-provider/app-config-provider';
import { NetworkStatusProvider } from './network-status-provider/network-status-provider';
import { PersistQueryFunctionsProvider } from './persist-query-functions-provider/persist-query-functions-provider';
import { UserOnboardingProvider } from './user-onboarding-provider/user-onboarding-provider';

const classicProvidersContextDefaultValue: ClassicProvidersContextInterface = {
  AppConfigProvider,
  ConfigProvider,
  ToastContainer,
  FDMServicesProvider,
  BackButtonUrlProvider,
  NetworkStatusProvider,
  CurrentUserProvider,
  UserOnboardingProvider,
  AppConfigFiltersProvider,
  PersistQueryFunctionsProvider,
  SDKProvider,
  AccessProvider,
};

type ClassicProvidersProps = PropsWithChildren<{
  i18nLanguage?: string;
  authClient: CogniteClient;
  queryClient: QueryClient;
  fdmServiceProviderMock?: {
    appConfig: AppConfig;
    client?: CogniteClient;
    user?: AuthStateUser;
  };
  userMock?: AuthStateUser;
}>;
// Hybrid is the Classic ALSO HAS DM IT IS A HYBRID PROJECT
export const ClassicProviders: FC<ClassicProvidersProps> = ({
  children,
  i18nLanguage,
  authClient,
  queryClient,
  fdmServiceProviderMock,
  userMock,
}) => {
  const {
    AppConfigProvider,
    ConfigProvider,
    ToastContainer,
    FDMServicesProvider,
    BackButtonUrlProvider,
    NetworkStatusProvider,
    CurrentUserProvider,
    UserOnboardingProvider,
    AppConfigFiltersProvider,
    PersistQueryFunctionsProvider,
    SDKProvider,
    AccessProvider,
  } =
    useContext(ClassicProvidersContext) ?? classicProvidersContextDefaultValue;

  return (
    <AppConfigProvider>
      <ConfigProvider
        localizationProviderProps={{
          adapterLocale: i18nLanguage,
        }}
        getOverlayContainer={getRootContainer}
      >
        <>
          <ToastContainer />
          <FDMServicesProvider mocks={fdmServiceProviderMock}>
            <BackButtonUrlProvider>
              <NetworkStatusProvider>
                <CurrentUserProvider>
                  <UserOnboardingProvider mocks={userMock}>
                    <AppConfigFiltersProvider>
                      <PersistQueryFunctionsProvider client={queryClient}>
                        <SDKProvider sdk={authClient}>
                          <AccessProvider>{children}</AccessProvider>
                        </SDKProvider>
                      </PersistQueryFunctionsProvider>
                    </AppConfigFiltersProvider>
                  </UserOnboardingProvider>
                </CurrentUserProvider>
              </NetworkStatusProvider>
            </BackButtonUrlProvider>
          </FDMServicesProvider>
        </>
      </ConfigProvider>
    </AppConfigProvider>
  );
};
